import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { RichTextField } from "./RichTextField";
import { toHtml } from "../lib/richtext";

const Ctx = createContext(null);

export function FieldCommentsProvider({ page, clientId, children }) {
  const [list, setList] = useState([]);
  const base = clientId ? `/admin/clients/${clientId}/comments/${page}` : `/client/comments/${page}`;
  const viewer = clientId ? "admin" : "cliente";
  useEffect(() => { api.get(base).then(({ data }) => setList(data || [])).catch(() => {}); }, [base]);
  const add = (c) => {
    setList((l) => [...l, { ...c, validated: true, author: viewer, author_name: viewer === "admin" ? "Fanny" : "Moi", created_at: new Date().toISOString() }]);
    api.post(base, c).catch(() => toast.error("Commentaire non enregistré, réessaie."));
  };
  const remove = (id) => { setList((l) => l.filter((x) => x.id !== id)); api.delete(`${base}/${id}`).catch(() => {}); };
  return <Ctx.Provider value={{ list, add, remove, viewer }}>{children}</Ctx.Provider>;
}

function CommentItem({ c, viewer, onRemove }) {
  const coach = (c.author || "admin") === "admin";
  const mine = (c.author || "admin") === viewer;
  const who = coach ? (viewer === "admin" ? "Moi" : "Fanny, ta coach") : (viewer === "cliente" ? "Moi" : c.author_name || "Ta cliente");
  return (
    <div data-testid={`field-comment-${c.id}`} className="flex gap-2.5 rounded-2xl bg-[#2D181E] p-3 text-white shadow-[0_8px_24px_rgba(45,24,30,0.18)]">
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${coach ? "bg-[#F3D2D9] text-[#590224]" : "bg-bordeaux text-white"}`}>{coach ? "F" : (c.author_name || "M").charAt(0).toUpperCase()}</span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold">{who}</span>
          {mine && <button type="button" data-testid={`field-comment-delete-${c.id}`} onClick={() => onRemove(c.id)} title="Supprimer" className="text-white/40 hover:text-white"><X size={13} /></button>}
        </div>
        {c.quote && <p className="mt-1 line-clamp-2 border-l-2 border-[#F3D2D9] pl-2 text-[11px] italic text-white/60">« {c.quote} »</p>}
        <p className="mt-1 whitespace-pre-wrap text-sm font-light leading-relaxed text-white/90">{c.text}</p>
      </div>
    </div>
  );
}

export function CommentField({ fieldKey, value, onChange, onBlur, readOnly = false, placeholder, className, dark, testid, empty }) {
  const ctx = useContext(Ctx);
  const wrap = useRef(null);
  const comments = ctx ? ctx.list.filter((c) => c.key === fieldKey && (c.text || "").trim() && ((c.author || "admin") === "admin" || (value || "").includes(`data-cid="${c.id}"`))) : [];
  const remove = (id) => {
    ctx.remove(id);
    const span = wrap.current?.querySelector(`[data-cid="${id}"]`);
    if (!span) return;
    const p = span.parentNode;
    while (span.firstChild) p.insertBefore(span.firstChild, span);
    p.removeChild(span);
    const field = wrap.current.querySelector("[data-field]");
    if (!readOnly && field) { onChange?.(field.innerHTML); onBlur?.(field.innerHTML); }
  };
  const onComment = ctx ? (c) => { ctx.add(c); if (!readOnly) onBlur?.(wrap.current.querySelector("[data-field]").innerHTML); } : undefined;
  const showEmpty = readOnly && !value && empty;
  return (
    <div ref={wrap} className="space-y-2">
      {showEmpty ? <div className={className}>{empty}</div> : (
        <RichTextField key={readOnly ? toHtml(value || "") : fieldKey} testid={testid} fieldKey={fieldKey} initialHtml={value || ""} readOnly={readOnly}
          onChange={onChange} onBlur={onBlur} onComment={onComment} placeholder={placeholder} dark={dark} className={className} />
      )}
      {comments.length > 0 && (
        <div data-testid={`${testid}-comments`} className="space-y-2">
          {comments.map((c) => <CommentItem key={c.id} c={c} viewer={ctx.viewer} onRemove={remove} />)}
        </div>
      )}
    </div>
  );
}
